import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, FileText, FileCheck, Calendar, Clock, Download, Send, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DistClientPrescriptionsPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rxRes, clientsRes] = await Promise.all([
          axios.get(`${API}/prescriptions/client/${clientId}`),
          axios.get(`${API}/users/clients`),
        ]);
        setPrescriptions(Array.isArray(rxRes.data) ? rxRes.data : []);
        const c = clientsRes.data.find(u => u.id === clientId);
        if (c) setClientName(c.name);
      } catch (err) {
        console.error('Error fetching prescriptions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [clientId]);

  const handleDownload = async (rx) => {
    try {
      const response = await axios.get(`${API}/prescriptions/${rx.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const d = new Date(rx.generated_at);
      const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
      const mesAnio = `${meses[d.getMonth()]} ${d.getFullYear()}`;
      link.setAttribute('download', `${rx.client_name} - Receta Medica - ${mesAnio}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch {
      toast.error('Error al descargar');
    }
  };

  const handleViewOnline = async (rx) => {
    try {
      const response = await axios.get(`${API}/prescriptions/${rx.id}/pdf?view=inline`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.error('Error al abrir la receta');
    }
  };

  const handleResendEmail = async (rx) => {
    try {
      toast.loading('Enviando al correo del cliente...', { id: 'resend' });
      await axios.post(`${API}/prescriptions/${rx.id}/send-email`);
      toast.success('Receta enviada al correo del cliente', { id: 'resend' });
      const res = await axios.get(`${API}/prescriptions/client/${clientId}`);
      setPrescriptions(res.data);
    } catch {
      toast.error('Error al enviar', { id: 'resend' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-red-100 border-t-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in pb-4" data-testid="dist-client-prescriptions-page">
      <button
        onClick={() => navigate(`/distributor/clients/${clientId}`)}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        data-testid="back-to-profile"
      >
        <ArrowLeft size={16} /> {clientName || 'Perfil'}
      </button>
      <div className="pt-1">
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Recetas Medicas
        </h1>
        <p className="text-sm text-gray-400 mt-1 font-medium">Recetas emitidas a {clientName}</p>
      </div>

      {prescriptions.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center" data-testid="no-prescriptions">
          <div className="w-20 h-20 bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText size={32} className="text-red-300" />
          </div>
          <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Sin recetas</p>
          <p className="text-sm text-gray-400 mt-1">Este cliente aun no tiene recetas medicas emitidas</p>
        </div>
      ) : (
        <div className="space-y-3">
          {prescriptions.map((rx, idx) => (
            <div
              key={rx.id}
              className="bg-white rounded-3xl border border-gray-100/50 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              style={{ animationDelay: `${idx * 80}ms` }}
              data-testid={`prescription-card-${rx.id}`}
            >
              <div className="h-1 bg-gradient-to-r from-red-400 via-rose-500 to-pink-500" />
              <div className="p-5 flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-red-50 to-rose-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileCheck size={22} className="text-red-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-sm leading-tight">{rx.template_name}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <div className="flex items-center gap-1.5">
                      <Calendar size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium">{rx.prescription_date}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium">
                        {new Date(rx.generated_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                  {rx.emailed && (
                    <span className="inline-flex items-center gap-1 mt-2.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full uppercase tracking-wider">
                      Enviado por email
                    </span>
                  )}
                </div>
              </div>
              <div className="mx-5 border-t border-dashed border-gray-200" />
              <div className="flex px-2 py-2">
                <button
                  onClick={() => handleViewOnline(rx)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 mx-1 text-sm font-semibold text-emerald-600 hover:bg-emerald-50 rounded-xl transition-all duration-200"
                  data-testid={`view-online-${rx.id}`}
                >
                  <Eye size={16} />
                  Ver Online
                </button>
                <button
                  onClick={() => handleDownload(rx)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 mx-1 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200"
                  data-testid={`download-${rx.id}`}
                >
                  <Download size={16} />
                  Descargar
                </button>
                <button
                  onClick={() => handleResendEmail(rx)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 mx-1 text-sm font-semibold text-rose-600 hover:bg-rose-50 rounded-xl transition-all duration-200"
                  data-testid={`resend-${rx.id}`}
                >
                  <Send size={16} />
                  Enviar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DistClientPrescriptionsPage;
